import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { api, type StoryList } from "../api/client";
import { Dots } from "../components/Dots";
import { InstallBanner } from "../components/InstallBanner";
import { StoryCard } from "../components/StoryCard";
import { TodayHero } from "../components/TodayHero";
import { track } from "../lib/events";
import { LEVELS } from "../lib/format";
import { useLoad } from "../lib/load";
import { useSession } from "../lib/session";
import { applyTheme } from "../lib/theme";

const TOPIC_ICONS: Record<string, string> = {
  All: "🌐",
  AI: "🤖",
  Security: "🛡️",
  "Dev tools": "🧰",
  Science: "🔬",
  Business: "📈",
  World: "🌍",
  Tech: "💻",
};

export function Today() {
  const { me, setMe, version, toast } = useSession();
  const [params, setParams] = useSearchParams();
  const { data: topics } = useLoad(() => api.topics(), []);
  const { data: languages } = useLoad(() => api.languages(), []);
  const defaultTopic = topics?.topics.includes("World") ? "World" : undefined;
  const topicParam = params.get("topic");
  // "All" sets topic=all explicitly: removing the param would fall back to the World default.
  const topic = topicParam === "all" ? undefined : (topicParam ?? defaultTopic);
  const languageParam = params.get("language");
  const language = languageParam === null ? "en" : languageParam;
  // First page reloads when the filter, the reader or their term state changes.
  const first = useLoad(
    () =>
      api.stories(topic, language || undefined).then((page) => {
        for (const s of page.items)
          for (const t of s.terms.slice(0, 3)) track("term_discovered", { term_id: t.id, story_id: s.id });
        return page;
      }),
    [topic, language, me?.id, version],
  );
  // Extra pages belong to one first page; a new first page discards them.
  const [more, setMore] = useState<{ base: StoryList; items: StoryList["items"]; cursor: string | null }>();
  const [loadingMore, setLoadingMore] = useState(false);

  const base = first.data;
  const extra = base && more?.base === base ? more : undefined;
  const items = [...(base?.items ?? []), ...(extra?.items ?? [])];
  const cursor = extra ? extra.cursor : (base?.next_cursor ?? null);

  const loadMore = async () => {
    if (!base || !cursor) return;
    setLoadingMore(true);
    try {
      const page = await api.stories(topic, language || undefined, cursor);
      setMore({ base, items: [...(extra?.items ?? []), ...page.items], cursor: page.next_cursor ?? null });
    } finally {
      setLoadingMore(false);
    }
  };
  const openTerm = (id: string) =>
    setParams((p) => {
      p.set("term", id);
      return p;
    });

  const level = me?.level ?? 1;
  const chips = ["All", ...(topics?.topics ?? [])];
  const selectedTopic = topicParam === "all" ? "All" : (topicParam ?? defaultTopic ?? "All");

  const toggleTheme = async () => {
    const theme = document.documentElement.dataset.theme === "dark" ? "light" : "dark";
    applyTheme(theme);
    if (!me) return;
    try {
      setMe(await api.patchMe({ theme }));
    } catch (error) {
      toast((error as Error).message);
    }
  };

  return (
    <>
      <header className="top">
        <div>
          <h1>Today</h1>
        </div>
        <div className="top-actions">
          <Link className="level-pill" to="/search" aria-label="Search">
            🔎 Search
          </Link>
          <Link className="level-pill" to="/you" aria-label={`Your level: ${LEVELS[level - 1]}. Change`}>
            <Dots level={level} /> {LEVELS[level - 1]}
          </Link>
          <button
            className="theme-toggle"
            type="button"
            aria-label="Toggle light and dark theme"
            title="Toggle light and dark theme"
            onClick={() => void toggleTheme()}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <g className="sun-icon">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
              </g>
              <path className="moon-icon" d="M20.2 15.1A8.5 8.5 0 0 1 8.9 3.8 8.5 8.5 0 1 0 20.2 15.1Z" />
            </svg>
          </button>
        </div>
      </header>
      <TodayHero items={items} />
      <label className="language-filter">
        <span aria-hidden="true">🗣️</span>
        <span>Language</span>
        <select
          aria-label="Filter stories by language"
          value={language}
          onChange={(event) =>
            setParams((p) => {
              p.set("language", event.target.value);
              return p;
            })
          }
        >
          <option value="">All languages</option>
          {[...new Set(["en", ...(languages?.languages ?? [])])].map((code) => (
            <option key={code} value={code}>
              {new Intl.DisplayNames(["en"], { type: "language" }).of(code) ?? code}
            </option>
          ))}
        </select>
      </label>
      <InstallBanner />
      <div className="filters" role="group" aria-label="Topics">
        {chips.map((t) => (
          <m.button
            key={t}
            type="button"
            aria-pressed={selectedTopic === t}
            whileTap={{ scale: 0.94 }}
            onClick={() =>
              setParams((p) => {
                p.set("topic", t === "All" ? "all" : t);
                return p;
              })
            }
          >
            {selectedTopic === t && (
              <m.span layoutId="topic-pill" className="chip-pill" aria-hidden="true" />
            )}
            <span className="chip-label">
              <span aria-hidden="true">{TOPIC_ICONS[t] ?? "📰"}</span> {t}
            </span>
          </m.button>
        ))}
      </div>
      {/* Grid remounts per topic: quick crossfade keeps the switch feeling continuous. */}
      <AnimatePresence mode="wait" initial={false}>
        <m.div
          className="feed-grid"
          key={selectedTopic}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
        >
          {first.error && items.length === 0 && (
            <p className="empty">Stories could not be loaded. Check your connection and try again.</p>
          )}
          {!first.error && first.loading && items.length === 0 && <p className="empty">Loading stories…</p>}
          {!first.error && !first.loading && items.length === 0 && (
            <p className="empty">No stories yet{topic ? ` in ${topic}` : ""}. New ones arrive as feeds are read.</p>
          )}
          {items.map((s, i) => (
            <StoryCard key={s.id} story={s} index={i} onTerm={openTerm} />
          ))}
          {cursor && (
            <m.button
              type="button"
              className="btn ghost wide-btn"
              onClick={loadMore}
              disabled={loadingMore}
              whileTap={{ scale: 0.97 }}
            >
              {loadingMore ? "Loading…" : "Show more stories"}
            </m.button>
          )}
        </m.div>
      </AnimatePresence>
    </>
  );
}
